/**
 * Frontend-owned catalog of agent-addressable UI targets.
 *
 * The backend agent only emits semantic `targetKey`s drawn from this catalog;
 * the frontend resolves each to a concrete route + DOM selector. This keeps the
 * backend ignorant of layout: re-skinning the UI never breaks the agent, and an
 * unknown key fails safe (see {@link resolveTarget}).
 *
 * Each target is marked in the DOM with `data-agent-target="<key>"`.
 */
import { DECLARATIONS_BASE } from "@/features/material-compliance/components/declarations/routes"
import {
  DEFAULT_SETTINGS_SECTION,
  isSettingsPagePath,
  SETTINGS_PATHS,
} from "@/features/settings/paths"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

export interface AgentTarget {
  /** Route the target lives on. Used for cross-page navigation. */
  route: string
  /** CSS selector resolving to the DOM element. */
  selector: string
  /**
   * Translation key for the human-readable label shown in
   * "Take me there → <label>" affordances. Preferred over `label`.
   */
  labelKey?: TranslationKeys
  /**
   * Raw, not-yet-localized label. Only still set by producers outside this
   * catalog (e.g. `declarationReviewAgentTargets`) that have not migrated to
   * `labelKey` yet — consumers resolve `labelKey` first and fall back to this.
   */
  label?: string
  /**
   * Override for how long to wait for the element after navigating. Page-level
   * targets are in the first render, but an element inside a record screen only
   * mounts once that record's fetch resolves — the default wait can expire
   * first and report the control as missing when it was merely not there yet.
   */
  waitTimeoutMs?: number
  /**
   * When the target is on more pages than `route` (every settings page
   * carries `settings.page`): the pages that already show it. Navigation
   * still goes to `route`.
   */
  presentOn?: (pathname: string) => boolean
}

/** Whether the page at `pathname` already shows `target`. */
export function isOnTargetRoute(
  target: Pick<AgentTarget, "route" | "presentOn">,
  pathname: string,
): boolean {
  return pathname === target.route || !!target.presentOn?.(pathname)
}

/** Build the canonical selector for a target key. */
export function targetSelector(targetKey: string): string {
  return `[data-agent-target="${targetKey}"]`
}

const CATALOG: Record<string, Omit<AgentTarget, "selector">> = {
  "kb.file-list": {
    route: "/knowledgebase",
    labelKey: "AI_CHAT/TARGET_LABEL_KB_FILES",
  },
  "kb.upload-button": {
    route: "/knowledgebase",
    labelKey: "AI_CHAT/TARGET_LABEL_KB_UPLOAD",
  },
  // Named ".list", not ".add": there is no standalone add-supplier control on
  // this page — every master-data entity is created only via the ERP import
  // flow (import.master-data). Matches the naming the sibling entity keys
  // (articles.list, products.list, customers.list) already use.
  "suppliers.list": {
    route: "/material-compliance/master-data/suppliers",
    labelKey: "AI_CHAT/TARGET_LABEL_SUPPLIERS_LIST",
  },
  // Legacy path 302s via LegacyStudioRedirect; point straight at the
  // post-restructure supplier-studio route instead of relying on the redirect.
  "suppliers.declaration-upload": {
    // Imported, not hardcoded: this key pointed at the pre-rename
    // `/supplier-declarations` path, which only still resolved via
    // LegacyStudioRedirect and broke the "already there?" check.
    route: DECLARATIONS_BASE,
    labelKey: "AI_CHAT/TARGET_LABEL_DECL_UPLOAD",
  },
  "import.master-data": {
    route: "/material-compliance/master-data/import",
    labelKey: "AI_CHAT/TARGET_LABEL_IMPORT_MD",
  },
  "questionnaire.new": {
    route: "/questionnaire/upload",
    labelKey: "AI_CHAT/TARGET_LABEL_QUESTIONNAIRE_NEW",
  },
  "history.list": {
    route: "/history",
    labelKey: "AI_CHAT/TARGET_LABEL_HISTORY",
  },
  "substances.section": {
    route: "/material-compliance/master-data/substances",
    labelKey: "AI_CHAT/TARGET_LABEL_SUBSTANCES",
  },
  "articles.list": {
    route: "/material-compliance/master-data/articles",
    labelKey: "AI_CHAT/TARGET_LABEL_ARTICLES",
  },
  "products.list": {
    route: "/material-compliance/master-data/products",
    labelKey: "AI_CHAT/TARGET_LABEL_PRODUCTS",
  },
  "customers.list": {
    route: "/material-compliance/master-data/customers",
    labelKey: "AI_CHAT/TARGET_LABEL_CUSTOMERS",
  },
  "suppliers.filters": {
    route: "/material-compliance/master-data/suppliers",
    labelKey: "AI_CHAT/TARGET_LABEL_SUPPLIER_FILTERS",
  },
  "articles.filters": {
    route: "/material-compliance/master-data/articles",
    labelKey: "AI_CHAT/TARGET_LABEL_ARTICLE_FILTERS",
  },
  "products.filters": {
    route: "/material-compliance/master-data/products",
    labelKey: "AI_CHAT/TARGET_LABEL_PRODUCT_FILTERS",
  },
  "customers.filters": {
    route: "/material-compliance/master-data/customers",
    labelKey: "AI_CHAT/TARGET_LABEL_CUSTOMER_FILTERS",
  },
  "substances.filters": {
    route: "/material-compliance/master-data/substances",
    labelKey: "AI_CHAT/TARGET_LABEL_SUBSTANCE_FILTERS",
  },
  "import.history": {
    route: "/material-compliance/master-data/import",
    labelKey: "AI_CHAT/TARGET_LABEL_IMPORT_HISTORY",
  },
  // NO import.commit-button (or import.confirm-mapping-button) entry here, and
  // its absence is the fix rather than an omission. Both controls live inside
  // the import FLOW — route `/master-data/mapping`, whose `?file_ids=` names the
  // staged files and whose stage is component state — so neither has a fixed
  // route to key on. The old entry named the UPLOAD page, which contains
  // neither: `resolveTargetKeysForRoute` matches by exact route, so standing on
  // the upload screen reported Commit as already visible, the agent announced it
  // had highlighted the button, and the pulse then timed out on an element that
  // was never in that page. They are built per action instead — see
  // `masterDataImportAgentTargets`, reached by the `open_mc_import` tool.
  // Lives inside an article's compliance table, so it needs that article open
  // first — the bare route reaches the list, not a record.
  "article.compliance-pill": {
    route: "/material-compliance/master-data/articles",
    labelKey: "AI_CHAT/TARGET_LABEL_COMPLIANCE_STATUS",
    waitTimeoutMs: 8000,
  },
  // Restructured under customer-studio/; was /material-compliance/customer-requests.
  "outreach.request": {
    route: "/material-compliance/customer-studio/requests",
    labelKey: "AI_CHAT/TARGET_LABEL_COMPLIANCE_REQUEST",
  },
  "outreach.request-import": {
    route: "/material-compliance/customer-studio/requests/import",
    labelKey: "AI_CHAT/TARGET_LABEL_REQUEST_IMPORT",
  },
  "customer-studio.dashboard": {
    route: "/material-compliance/customer-studio/dashboard",
    labelKey: "AI_CHAT/TARGET_LABEL_CUSTOMER_STUDIO_DASHBOARD",
  },
  "conformity.inbox": {
    route: "/material-compliance/customer-studio/conformity",
    labelKey: "AI_CHAT/TARGET_LABEL_CONFORMITY_INBOX",
  },
  "conformity.new": {
    route: "/material-compliance/customer-studio/conformity/new",
    labelKey: "AI_CHAT/TARGET_LABEL_CONFORMITY_NEW",
  },
  "supplier-studio.dashboard": {
    route: "/material-compliance/supplier-studio/dashboard",
    labelKey: "AI_CHAT/TARGET_LABEL_SUPPLIER_STUDIO_DASHBOARD",
  },
  "declarations.list": {
    route: "/material-compliance/supplier-studio/declarations",
    labelKey: "AI_CHAT/TARGET_LABEL_DECLARATIONS_LIST",
  },
  "inquiry.list": {
    route: "/material-compliance/supplier-studio/inquiry",
    labelKey: "AI_CHAT/TARGET_LABEL_INQUIRIES",
  },
  "inquiry.new": {
    route: "/material-compliance/supplier-studio/inquiry/new",
    labelKey: "AI_CHAT/TARGET_LABEL_INQUIRY_NEW",
  },
  "inquiry.draft": {
    route: "/material-compliance/supplier-studio/inquiry/draft",
    labelKey: "AI_CHAT/TARGET_LABEL_INQUIRY_DRAFT",
  },
  // The first element-level target in this catalog; everything above is a page,
  // list or filter. The route has no `ids` query, so resolving it cross-page
  // reaches an empty composer — `openInquiryDraft` is the id-carrying path, and
  // this entry exists so the button can be pulsed once the user is there.
  "inquiry.send-button": {
    route: "/material-compliance/supplier-studio/inquiry/draft",
    labelKey: "AI_CHAT/TARGET_LABEL_INQUIRY_SEND",
    // The composer renders an empty shell until the inquiry fetch resolves, so
    // this button is not in the DOM on arrival.
    waitTimeoutMs: 8000,
  },
  "company-info.page": {
    route: "/company-info",
    labelKey: "AI_CHAT/TARGET_LABEL_COMPANY_INFO",
  },
  // Reuses the chat history page's own title — same screen, same label.
  "chat.history": {
    route: "/chat/history",
    labelKey: "AI_CHAT/HISTORY/PAGE_TITLE",
  },
  "notifications.center": {
    route: "/notifications",
    labelKey: "AI_CHAT/TARGET_LABEL_NOTIFICATIONS",
  },
  "notifications.settings": {
    route: SETTINGS_PATHS.notifications,
    labelKey: "AI_CHAT/TARGET_LABEL_NOTIFICATION_SETTINGS",
  },
  // Every settings page carries this target; a highlight from outside opens
  // the default page.
  "settings.page": {
    route: SETTINGS_PATHS[DEFAULT_SETTINGS_SECTION],
    // Not the template editor: it lives under /settings without the target.
    presentOn: isSettingsPagePath,
    labelKey: "AI_CHAT/TARGET_LABEL_SETTINGS",
  },
}

export const AGENT_TARGETS: Record<string, AgentTarget> = Object.fromEntries(
  Object.entries(CATALOG).map(([key, value]) => [
    key,
    { ...value, selector: targetSelector(key) },
  ]),
)

/** Resolve a target key to its full descriptor, or `null` if unknown. */
export function resolveTarget(targetKey: string): AgentTarget | null {
  return AGENT_TARGETS[targetKey] ?? null
}

/**
 * Reverse lookup: every target key whose route matches `pathname` exactly, or
 * whose `presentOn` takes it in (query string ignored — target routes never
 * carry one). Lets the backend be told which target_key(s), if any,
 * describe where the user already is, instead of guessing from the raw
 * pathname it has no route table for.
 */
export function resolveTargetKeysForRoute(pathname: string): string[] {
  return Object.entries(AGENT_TARGETS)
    .filter(([, target]) => isOnTargetRoute(target, pathname))
    .map(([key]) => key)
}
